/**
 * 줄서기 access_token 보관 — 재방문 시 상태 복원용.
 *
 * @see #781 / admin#2111 설계 §4
 *
 * ## 왜 localStorage 인가
 * 고객은 등록 후 브라우저를 닫고 밖에서 기다린다(설계 §6.1 "어디서든 기다리세요").
 * sessionStorage 는 탭을 닫으면 사라져 대기 중인 팀이 자기 순번을 잃는다.
 *
 * ## 매장별 분리
 * 토큰은 매장 단위로 발급되므로 키에 storeId 를 포함한다 — 다른 매장 줄에 섰을 때
 * 이전 매장의 토큰으로 조회해 404/오표시가 나는 것을 막는다.
 *
 * ## 종결 시 제거
 * seated/cancelled 는 되돌릴 수 없다. 종결 토큰을 남기면 재방문 시 항상 종결 화면이
 * 떠서 재등록 동선이 막히므로 호출 측이 `clearWaitlistToken` 으로 지운다.
 */
import { logger } from '@/utils/logger';
import type { WaitlistQueueType } from '@/lib/api/waitlist';

const TOKEN_KEY_PREFIX = 'olulo_waitlist_token_';
const MODE_KEY_PREFIX = 'olulo_waitlist_mode_';
const MARKETING_CONSENT_KEY_PREFIX = 'olulo_waitlist_marketing_consent_';

/** 플래그의 유일한 참값. 다른 값이 들어 있으면 "동의 없음"으로 취급한다. */
const MARKETING_CONSENT_FLAG = '1';

function tokenKey(storeId: number): string {
  return `${TOKEN_KEY_PREFIX}${storeId}`;
}

function modeKey(storeId: number): string {
  return `${MODE_KEY_PREFIX}${storeId}`;
}

function marketingConsentKey(storeId: number): string {
  return `${MARKETING_CONSENT_KEY_PREFIX}${storeId}`;
}

export function readWaitlistToken(storeId: number): string | null {
  try {
    return window.localStorage.getItem(tokenKey(storeId));
  } catch (error) {
    // Safari 프라이빗 모드 등 localStorage 접근 차단 — 토큰 없음으로 취급(등록 화면).
    logger.warn('[waitlist] access_token 읽기 실패', error);
    return null;
  }
}

export function saveWaitlistToken(storeId: number, accessToken: string): void {
  try {
    window.localStorage.setItem(tokenKey(storeId), accessToken);
  } catch (error) {
    // 저장 실패 시에도 현재 세션의 화면 전환은 그대로 진행된다 — 새로고침 시에만 복원 불가.
    logger.warn('[waitlist] access_token 저장 실패', error);
  }
}

/**
 * 토큰 폐기 — 마케팅 동의 플래그도 함께 지운다.
 *
 * 철회 링크는 "지금 이 토큰으로 철회할 수 있다"는 뜻이다. 토큰이 사라진 뒤에도
 * 플래그만 남으면 다음 등록 때 철회 대상이 없는 링크가 떠 사용자를 오도한다 —
 * 토큰과 생명주기를 묶어 둔다(이탈/재등록 복귀/404 정리 모두 이 함수를 지난다).
 */
export function clearWaitlistToken(storeId: number): void {
  try {
    window.localStorage.removeItem(tokenKey(storeId));
  } catch (error) {
    logger.warn('[waitlist] access_token 제거 실패', error);
  }
  clearWaitlistMarketingConsent(storeId);
}

/**
 * 마케팅 동의 기록 여부 플래그 — 셀프 철회 링크 노출 조건(admin#2472 / menu 본 변경).
 *
 * ## 왜 로컬 플래그인가
 * 동의 증거의 SSOT 는 서버다. 다만 고객 화면에는 "내 동의 상태"를 조회하는 무인증
 * 엔드포인트가 없고, 있더라도 폴링마다 개인정보성 상태를 되묻는 것은 과하다.
 * 등록 응답이 `marketing_consent.status === 'recorded'` 로 알려준 사실만 매장별로
 * 기억해 링크 노출을 결정하고, 실제 철회 여부는 서버 응답(200/404)이 확정한다.
 *
 * 값 자체에는 동의 문구·연락처 등 어떤 개인정보도 담지 않는다 — `'1'` 뿐이다.
 */
export function readWaitlistMarketingConsent(storeId: number): boolean {
  try {
    return window.localStorage.getItem(marketingConsentKey(storeId)) === MARKETING_CONSENT_FLAG;
  } catch (error) {
    // 접근 차단 시 "동의 기록 없음"으로 폴백 — 철회 링크를 숨긴다(허위 안내 방지).
    logger.warn('[waitlist] 마케팅 동의 플래그 읽기 실패', error);
    return false;
  }
}

export function saveWaitlistMarketingConsent(storeId: number): void {
  try {
    window.localStorage.setItem(marketingConsentKey(storeId), MARKETING_CONSENT_FLAG);
  } catch (error) {
    // 저장 실패는 링크 미노출로만 이어진다 — 서버의 동의 증거는 그대로다.
    logger.warn('[waitlist] 마케팅 동의 플래그 저장 실패', error);
  }
  // 새 동의가 기록됐으면 직전 철회 기억은 더 이상 사실이 아니다 — 남겨두면 방금
  // 다시 동의한 고객에게 "수신하지 않습니다" 완료 문구를 보여주게 된다.
  clearWaitlistMarketingWithdrawn();
}

export function clearWaitlistMarketingConsent(storeId: number): void {
  try {
    window.localStorage.removeItem(marketingConsentKey(storeId));
  } catch (error) {
    logger.warn('[waitlist] 마케팅 동의 플래그 제거 실패', error);
  }
}

/**
 * 모든 매장의 마케팅 동의 플래그 제거 — 셀프 철회는 **연락처 전역**이기 때문이다.
 *
 * 서버의 철회는 이 번호(vault)의 미철회 동의 증거를 전부 철회하고 전역 억제를 세운다
 * (admin#2491 §11.8). 그런데 로컬 플래그는 매장별로 흩어져 있어, 철회한 매장의 키만
 * 지우면 다른 매장 줄서기 화면에는 "철회하세요" 링크가 그대로 남는다 — 이미 전역으로
 * 꺼진 동의를 다시 끄라고 권하는 셈이라 고객에게 상태를 잘못 알려준다.
 *
 * 매장 목록을 알 수 없으므로 프리픽스로 저장소를 훑는다. 순회 중 제거하면 인덱스가
 * 밀리므로 대상 키를 먼저 모은 뒤 지운다.
 *
 * ## 호출 조건 — 서버가 상태를 되짚어 줄 수 있을 때만
 * localStorage 는 단말 전역이지 **연락처 전역이 아니다**. 공유 단말에서 다른 번호로
 * 다른 매장 줄에 선 경우 그 연락처의 플래그까지 지운다. 신서버(`marketing_consent`
 * 를 내려주는 쪽)에서는 다음 조회의 `current` 가 각 매장의 링크를 다시 정확히 그려
 * 주지만, 구서버에서는 플래그가 유일한 근거라 되돌릴 방법이 없다 — 그래서 호출부
 * (useMarketingConsentWithdrawal)는 신서버 200 경로에서만 이 함수를 쓴다.
 */
export function clearAllWaitlistMarketingConsents(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      // `typeof` 로 거르는 이유: 테스트 스텁 등 비표준 구현은 null 대신 undefined 를
      // 돌려줄 수 있어 `!== null` 만으로는 startsWith 에서 TypeError 가 난다.
      if (typeof key === 'string' && key.startsWith(MARKETING_CONSENT_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      window.localStorage.removeItem(key);
    }
  } catch (error) {
    // 접근 차단(Safari 프라이빗 모드)·비표준 저장소 구현 — 서버 철회 결과는 그대로다.
    logger.warn('[waitlist] 마케팅 동의 플래그 전체 제거 실패', error);
  }
}

/**
 * 방금 철회를 마쳤다는 사실의 단말 기억 — 새로고침·재진입을 넘기기 위한 것(#884).
 *
 * ## 왜 필요한가
 * 철회 완료는 메모리(훅 state)에만 있었다. 그런데 서버의 `marketing_consent.current`
 * 는 전파·캐시 지연으로 잠시 true 로 남을 수 있어, 철회 직후 새로고침하거나 WhatsApp
 * 알림 링크로 되돌아오면 "철회하기" 링크가 다시 떠서 방금 끝낸 일을 또 권한다.
 *
 * ## 왜 토큰까지 함께 적는가
 * localStorage 는 단말 전역이지 연락처 전역이 아니다(공유 단말에서 다른 번호로 다른
 * 매장 줄에 선 경우가 있다). 토큰 없이 "철회했음"만 적으면 그 다른 연락처 화면에도
 * 완료 문구가 떠 거짓 안내가 된다 — 철회를 수행한 access_token 에만 적용한다.
 * 토큰 값 자체는 이미 `olulo_waitlist_token_*` 에 저장돼 있어 새로운 노출면이 아니다.
 *
 * ## 왜 짧은 TTL 인가
 * 이 기억은 "서버가 아직 못 따라왔을 동안"만 유효한 임시 진실이다. 오래 남기면 다른
 * 경로(다른 단말·직원 대행)로 다시 동의한 고객에게까지 완료 문구가 남는다.
 */
const MARKETING_WITHDRAWN_KEY = 'olulo_waitlist_marketing_withdrawn';

/** 완료 기억의 유효 기간 — 서버 전파 지연을 넉넉히 덮되 하루를 넘기지 않는다. */
const MARKETING_WITHDRAWN_TTL_MS = 6 * 60 * 60 * 1000;

interface MarketingWithdrawnRecord {
  token: string;
  at: number;
}

export function saveWaitlistMarketingWithdrawn(accessToken: string): void {
  try {
    const record: MarketingWithdrawnRecord = { token: accessToken, at: Date.now() };
    window.localStorage.setItem(MARKETING_WITHDRAWN_KEY, JSON.stringify(record));
  } catch (error) {
    // 저장 실패는 "새로고침하면 완료 문구가 사라진다"로만 이어진다(서버 철회는 그대로).
    logger.warn('[waitlist] 마케팅 철회 완료 기록 저장 실패', error);
  }
}

/**
 * 이 토큰으로 최근(TTL 이내) 철회를 마쳤는가.
 *
 * 만료·손상된 기록은 읽는 김에 지운다 — 다음 진입에서 같은 파싱을 반복하지 않는다.
 */
export function readWaitlistMarketingWithdrawn(accessToken: string): boolean {
  try {
    const raw = window.localStorage.getItem(MARKETING_WITHDRAWN_KEY);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as Partial<MarketingWithdrawnRecord> | null;
    const at = typeof parsed?.at === 'number' ? parsed.at : NaN;
    // 시계를 되돌린 단말에서 미래 시각이 찍혀도 만료로 취급한다(음수 경과).
    const elapsed = Date.now() - at;
    if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > MARKETING_WITHDRAWN_TTL_MS) {
      clearWaitlistMarketingWithdrawn();
      return false;
    }
    return parsed?.token === accessToken;
  } catch (error) {
    // 접근 차단·JSON 손상 — 완료 기억 없음으로 폴백(서버 값만 보고 그린다).
    logger.warn('[waitlist] 마케팅 철회 완료 기록 읽기 실패', error);
    return false;
  }
}

export function clearWaitlistMarketingWithdrawn(): void {
  try {
    window.localStorage.removeItem(MARKETING_WITHDRAWN_KEY);
  } catch (error) {
    logger.warn('[waitlist] 마케팅 철회 완료 기록 제거 실패', error);
  }
}

/**
 * 줄서기 2종 확장(테이블+픽업) — 선택 화면 복귀용 모드 보관(#794).
 *
 * 둘 다 열린 매장에서 사용자가 한 번 고른 모드(테이블/픽업)를 기억해, 새로고침이나
 * 재방문 시 선택 화면(ChooseScreen)을 다시 거치지 않고 바로 해당 큐의 등록/상태
 * 화면으로 복귀시키기 위한 것 — access_token 과 달리 서버 자격증명이 아니라
 * 순수 UI 상태이므로 별도 키 프리픽스(`olulo_waitlist_mode_`)를 쓴다.
 */
export function readWaitlistMode(storeId: number): WaitlistQueueType | null {
  try {
    const value = window.localStorage.getItem(modeKey(storeId));
    return value === 'table' || value === 'pickup' ? value : null;
  } catch (error) {
    // Safari 프라이빗 모드 등 localStorage 접근 차단 — 모드 없음으로 취급(선택 화면).
    logger.warn('[waitlist] 모드 읽기 실패', error);
    return null;
  }
}

export function saveWaitlistMode(storeId: number, mode: WaitlistQueueType): void {
  try {
    window.localStorage.setItem(modeKey(storeId), mode);
  } catch (error) {
    // 저장 실패 시에도 현재 세션의 화면 전환은 그대로 진행된다 — 새로고침 시에만 복원 불가.
    logger.warn('[waitlist] 모드 저장 실패', error);
  }
}

export function clearWaitlistMode(storeId: number): void {
  try {
    window.localStorage.removeItem(modeKey(storeId));
  } catch (error) {
    logger.warn('[waitlist] 모드 제거 실패', error);
  }
}
